/**
 * A3S UI Theme — re-exports design tokens with Unistyles type augmentation.
 *
 * React Native consumers should import from this module (or the root
 * `@visioni/ui`) to get correctly typed `useUnistyles()` and
 * `StyleSheet.create()` via the `declare module` augmentation below.
 *
 * Non-RN consumers (Node CLIs, build scripts) should import from
 * `@visioni/ui/tokens` instead to avoid the `react-native-unistyles`
 * dependency.
 *
 * NOTE: This module does NOT call `StyleSheet.configure()`. The app entry
 * point is responsible for configuration.
 */
import "react-native-unistyles";

// ---------------------------------------------------------------------------
// Color palettes
// ---------------------------------------------------------------------------

export const colorPalettes = {
  navy: {
    100: "#C3C6F4",
    200: "#8A91E6",
    300: "#5761D8",
    400: "#3443C4",
    500: "#2431A0",
    600: "#17207A",
    700: "#0C105F",
    750: "#0A105B",
    800: "#090C4F",
    900: "#06083B",
    1000: "#040630",
  },
  red: {
    100: "#FFF2F6",
    200: "#FEE3EC",
    300: "#FCC0D0",
    400: "#F99CB4",
    500: "#F67997",
    600: "#F23D6F",
    700: "#DB2D5D",
    800: "#B81F4C",
    900: "#93193F",
    1000: "#6F1432",
  },
  grey: {
    50: "#F8FAFC",
    100: "#F1F5F9",
    200: "#E2E8F0",
    300: "#CBD5E1",
    400: "#94A3B8",
    500: "#64748B",
    600: "#475569",
    700: "#334155",
    800: "#1F2937",
    900: "#111827",
    925: "#0E1828",
    950: "#0B1220",
    1000: "#000000",
  },
  cyan: {
    50: "#EAFBFF",
    100: "#CFF5FF",
    200: "#A7ECFF",
    300: "#70DEFF",
    400: "#3FD2FF",
    500: "#12C9FD",
    600: "#0FB4E3",
    700: "#0C96BD",
    800: "#0A7897",
    900: "#085B71",
    950: "#085B71",
    1000: "#085B71",
  },
  blue: {
    50: "#F0F3FF",
    100: "#D8E0FF",
    200: "#B8C3FF",
    300: "#90A1FF",
    400: "#617BFF",
    450: "#5E5CE6",
    475: "#3C61DD",
    500: "#2D52EC",
    600: "#2648D4",
    700: "#162F9D",
    800: "#16254F",
    900: "#141E34",
    1000: "#0D1525",
  },
  purple: {
    50: "#F7EEFE",
    100: "#E9CCFA",
    200: "#D29AEC",
    300: "#B96AD4",
    400: "#A44BCA",
    500: "#8F2FC0",
    600: "#7715A2",
    700: "#631085",
    800: "#4F0C69",
    900: "#3B094E",
  },
  danger: {
    50: "#FFF2F6",
    100: "#FFCCCC",
    200: "#FF9999",
    300: "#FF6666",
    400: "#FF3333",
    500: "#CC0000",
    600: "#A30000",
    700: "#7A0000",
    800: "#520000",
    900: "#290000",
  },
  success: {
    50: "#ECFDF5",
    100: "#D1FAE5",
    200: "#A7F3D0",
    300: "#6EE7B7",
    400: "#34D399",
    500: "#10B981",
    600: "#059669",
    700: "#047857",
    800: "#065F46",
    900: "#064E3B",
  },
  warning: {
    50: "#FFFBEB",
    100: "#FEF3C7",
    200: "#FDE68A",
    300: "#FCD34D",
    400: "#FBBF24",
    500: "#F59E0B",
    600: "#D97706",
    700: "#B45309",
    800: "#92400E",
    900: "#78350F",
  },
  indigo: {
    50: "#EEF2FF",
    100: "#E0E7FF",
    200: "#C7D2FE",
    300: "#A5B4FC",
    400: "#818CF8",
    500: "#6366F1",
    600: "#4F46E5",
    700: "#4338CA",
    800: "#3730A3",
    900: "#312E81",
  },
  teal: {
    50: "#F0FDFA",
    100: "#CCFBF1",
    200: "#99F6E4",
    300: "#5EEAD4",
    400: "#2DD4BF",
    500: "#14B8A6",
    600: "#0D9488",
    700: "#0F766E",
    800: "#115E59",
    900: "#134E4A",
  },
  white: {
    default: "#FFFFFF",
    50: "#FFFFFF0A",
    100: "#FFFFFF1A",
    200: "#FFFFFF33",
    300: "#FFFFFF4D",
    400: "#FFFFFF66",
    500: "#FFFFFF80",
    600: "#FFFFFF99",
    700: "#FFFFFFB3",
    800: "#FFFFFFCC",
    850: "#FFFFFFE0",
    900: "#FFFFFFE6",
    1000: "#FFFFFFFF",
  },
  black: {
    default: "#000000",
    50: "#0000000A",
    100: "#0000001A",
    200: "#00000033",
    300: "#0000004D",
    400: "#00000066",
    500: "#00000080",
    600: "#00000099",
    700: "#000000B3",
    800: "#000000CC",
    900: "#000000E6",
    1000: "#000000FF",
  },
} as const;

// ---------------------------------------------------------------------------
// Spacing
// ---------------------------------------------------------------------------

export const spacing = {
  none: 0,
  xxs: 2,
  xs: 4,
  sm: 6,
  md: 8,
  lg: 10,
  xl: 12,
  "2xl": 14,
  "3xl": 16,
  "4xl": 18,
  "5xl": 20,
  "6xl": 24,
  "7xl": 28,
  "8xl": 32,
  "9xl": 36,
  "10xl": 40,
  "11xl": 48,
  "12xl": 56,
  "13xl": 64,
  "14xl": 80,
  "15xl": 96,
} as const;

// ---------------------------------------------------------------------------
// Shadows
// ---------------------------------------------------------------------------

export const shadows = {
  sm: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  md: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 3,
  },
  lg: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.15,
    shadowRadius: 15,
    elevation: 5,
  },
  xl: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 20 },
    shadowOpacity: 0.2,
    shadowRadius: 25,
    elevation: 8,
  },
  "2xl": {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 25 },
    shadowOpacity: 0.25,
    shadowRadius: 50,
    elevation: 12,
  },
} as const;

// ---------------------------------------------------------------------------
// Z-Index
// ---------------------------------------------------------------------------

export const zIndex = {
  base: 0,
  input: 1,
  inputOpen: 50,
  backdrop: 15,
  anchor: 16,
  dropdown: 20,
  /**
   * `modal` and `toast` are the only values here applied at the document root
   * (a portaled overlay); the rest stack within a page. They therefore have to
   * clear the ad-hoc z-indexes scattered through the app rather than sit on the
   * same small scale as the page-local tokens — clip player chrome reaches
   * 10000, web drawers and video controls 9999, and vehicle filter panels 2000.
   * At the old 100 an overlay lost to all of them, painting and hit-testing
   * underneath page content.
   */
  modal: 10100,
  toast: 10200,
} as const;

export const lightTheme = {
  currentTheme: "light",
  colors: {
    ...colorPalettes,
    background: colorPalettes.grey[50],
    backgroundSecondary: colorPalettes.navy[750],
    foreground: colorPalettes.grey[100],
    surface: colorPalettes.white.default,
    surfaceCard: colorPalettes.white.default,
    surfaceCardInner: colorPalettes.grey[50],
    surfaceCardInnerWarning: colorPalettes.warning[50],
    surface1: {
      background: colorPalettes.white.default,
      foregroundPrimary: colorPalettes.black.default,
      foregroundSecondary: colorPalettes.grey[500],
      foregroundTertiary: colorPalettes.grey[100],
      hover: colorPalettes.blue[600],
    },
    surface2: {
      hover: colorPalettes.grey[100],
      background: colorPalettes.grey[50],
      selected: colorPalettes.grey[100],
    },
    muted: colorPalettes.grey[200],
    skeleton: colorPalettes.grey[200],
    text: colorPalettes.grey[900],
    textReversed: colorPalettes.grey[50],
    textMuted: colorPalettes.grey[500],
    textLabel: colorPalettes.grey[925],
    field: {
      border_default: colorPalettes.grey[400],
    },
    textSecondary: colorPalettes.grey[400],
    icon: colorPalettes.grey[500],
    iconPrimary: colorPalettes.grey[700],
    border: colorPalettes.black[100],
    borderCard: colorPalettes.grey[200],
    interactive: {
      controls: {
        default_background: colorPalettes.white.default,
        secondary_background: colorPalettes.blue[800],
        default_midground: colorPalettes.grey[500],
        secondary_midground: colorPalettes.grey[500],
        secondary_foreground: colorPalettes.blue[400],
        selected_midground: colorPalettes.blue[500],
        selected_foreground: colorPalettes.white.default,
        disabled_background: colorPalettes.grey[300],
        disabled_active_background: colorPalettes.blue[500],
        disabled_active_secondary_background: colorPalettes.grey[50],
        hover_midground: colorPalettes.blue[700],
        focused_border: colorPalettes.black.default,
        focused_border_outline: colorPalettes.blue[475],
      },
      buttons: {
        default_background: colorPalettes.blue[500],
        default_foreground: colorPalettes.white.default,
        secondary_foreground: colorPalettes.blue[500],
        hover_background: colorPalettes.blue[700],
        hover_foreground: colorPalettes.white.default,
        pressed_background: colorPalettes.blue[900],
        pressed_foreground: colorPalettes.white.default,
        disabled_background: colorPalettes.grey[300],
        disabled_foreground: colorPalettes.grey[400],
        focused_border: colorPalettes.black.default,
        background_icon_button: colorPalettes.grey[100],
      },
      secondary: {
        default_background: colorPalettes.grey[100],
        background_secondary: colorPalettes.grey[100],
        default_border: colorPalettes.blue[100],
      },
    },
    alarmStates: {
      armed: colorPalettes.purple[500],
      armedSecondary: colorPalettes.purple[700],
      armedBorder: colorPalettes.purple[900],
      armedBorderSecondary: colorPalettes.purple[400],
      disarmed: colorPalettes.grey[200],
      disarmedBadge: colorPalettes.warning[700],
      disarmedBorder: colorPalettes.warning[400],
      trackBackground: colorPalettes.grey[200],
      pastTrackBackground: colorPalettes.grey[50],
      routinePreviewBorder: colorPalettes.purple[600],
    },
    alarmStateBadges: {
      armed: {
        background: colorPalettes.purple[100],
        border: colorPalettes.purple[300],
        foreground: colorPalettes.purple[600],
      },
      disarmed: {
        background: colorPalettes.warning[100],
        border: colorPalettes.warning[400],
        foreground: colorPalettes.warning[700],
      },
      modified: {
        background: colorPalettes.blue[100],
        border: colorPalettes.blue[300],
        foreground: colorPalettes.blue[600],
      },
      removed: {
        background: colorPalettes.danger[100],
        border: colorPalettes.danger[300],
        foreground: colorPalettes.danger[600],
      },
    },
    exceptionPreviewCards: {
      removed: { border: colorPalettes.danger[400] },
      modified: { border: colorPalettes.blue[400] },
    },
    status: {
      error: colorPalettes.danger[500],
      warning: colorPalettes.warning[600],
      success: colorPalettes.success[600],
      contact_link: colorPalettes.red[700],
    },
    statusBadge: {
      liveBackground: colorPalettes.success[600],
      liveBorder: colorPalettes.success[500],
      offlineBackground: colorPalettes.danger[500],
      offlineBorder: colorPalettes.danger[400],
    },
    mediaOverlay: {
      backgroundLight: colorPalettes.black[300],
      backgroundMedium: colorPalettes.black[500],
      foreground: colorPalettes.white.default,
      // Letterbox backdrop behind a video frame. Deliberately identical in
      // light and dark: it surrounds the video itself, so it stays black in
      // both themes rather than following the page surface.
      videoSurface: colorPalettes.black.default,
      // Unplayed portion of a scrub bar. Sits over the video surface above,
      // so it is a translucent white in both themes for the same reason.
      scrubTrack: colorPalettes.white[300],
    },
    system: {
      positive_subtle: colorPalettes.success[800],
    },
  },
  spacing,
  shadows,
  zIndex,
} as const;

export const darkTheme = {
  currentTheme: "dark",
  colors: {
    ...colorPalettes,
    background: colorPalettes.grey[950],
    backgroundSecondary: colorPalettes.navy[750],
    foreground: colorPalettes.grey[900],
    surface: colorPalettes.grey[900],
    surfaceCard: colorPalettes.grey[800],
    surfaceCardInner: colorPalettes.grey[800],
    surfaceCardInnerWarning: colorPalettes.warning[50],
    surface1: {
      background: colorPalettes.grey[800],
      foregroundPrimary: colorPalettes.white.default,
      foregroundSecondary: colorPalettes.grey[500],
      foregroundTertiary: colorPalettes.grey[700],
      hover: colorPalettes.blue[800],
    },
    surface2: {
      hover: colorPalettes.blue[800],
      background: colorPalettes.grey[900],
      selected: colorPalettes.grey[950],
    },
    muted: colorPalettes.grey[800],
    skeleton: colorPalettes.grey[600],
    text: colorPalettes.grey[50],
    textReversed: colorPalettes.grey[900],
    textMuted: colorPalettes.grey[400],
    textLabel: colorPalettes.grey[300],
    field: {
      border_default: colorPalettes.grey[400],
    },
    textSecondary: colorPalettes.grey[300],
    icon: colorPalettes.grey[300],
    iconPrimary: colorPalettes.grey[200],
    border: colorPalettes.white[100],
    borderCard: colorPalettes.grey[700],
    interactive: {
      controls: {
        default_background: colorPalettes.grey[800],
        secondary_background: colorPalettes.blue[800],
        default_midground: colorPalettes.grey[500],
        secondary_midground: colorPalettes.grey[300],
        secondary_foreground: colorPalettes.blue[400],
        selected_midground: colorPalettes.blue[400],
        selected_foreground: colorPalettes.blue[900],
        disabled_background: colorPalettes.grey[700],
        disabled_active_background: colorPalettes.blue[500],
        disabled_active_secondary_background: colorPalettes.grey[100],
        hover_midground: colorPalettes.blue[200],
        focused_border: colorPalettes.white.default,
        focused_border_outline: colorPalettes.blue[475],
      },
      buttons: {
        default_background: colorPalettes.blue[500],
        default_foreground: colorPalettes.white.default,
        secondary_foreground: colorPalettes.blue[500],
        hover_background: colorPalettes.blue[400],
        hover_foreground: colorPalettes.blue[900],
        pressed_background: colorPalettes.blue[300],
        disabled_background: colorPalettes.grey[700],
        disabled_foreground: colorPalettes.grey[500],
        focused_border: colorPalettes.white.default,
        background_icon_button: colorPalettes.grey[900],
        foreground_icon_button: colorPalettes.white.default,
      },
      secondary: {
        default_background: colorPalettes.grey[800],
        background_secondary: colorPalettes.grey[900],
        default_border: colorPalettes.blue[700],
      },
    },
    alarmStates: {
      armed: colorPalettes.purple[500],
      armedSecondary: colorPalettes.purple[900],
      armedBorder: colorPalettes.purple[900],
      armedBorderSecondary: colorPalettes.purple[400],
      disarmed: colorPalettes.grey[200],
      disarmedBadge: colorPalettes.warning[600],
      disarmedBorder: colorPalettes.warning[400],
      trackBackground: colorPalettes.grey[700],
      pastTrackBackground: colorPalettes.grey[800],
      routinePreviewBorder: colorPalettes.purple[600],
    },
    alarmStateBadges: {
      armed: {
        background: colorPalettes.purple[900],
        border: colorPalettes.purple[600],
        foreground: colorPalettes.purple[200],
      },
      disarmed: {
        background: colorPalettes.warning[900],
        border: colorPalettes.warning[700],
        foreground: colorPalettes.warning[300],
      },
      modified: {
        background: colorPalettes.blue[900],
        border: colorPalettes.blue[600],
        foreground: colorPalettes.blue[300],
      },
      removed: {
        background: colorPalettes.danger[900],
        border: colorPalettes.danger[600],
        foreground: colorPalettes.danger[300],
      },
    },
    exceptionPreviewCards: {
      removed: { border: colorPalettes.danger[600] },
      modified: { border: colorPalettes.blue[600] },
    },
    status: {
      error: colorPalettes.danger[400],
      warning: colorPalettes.warning[500],
      success: colorPalettes.success[600],
      contact_link: colorPalettes.red[700],
    },
    statusBadge: {
      liveBackground: colorPalettes.success[600],
      liveBorder: colorPalettes.success[500],
      offlineBackground: colorPalettes.danger[500],
      offlineBorder: colorPalettes.danger[400],
    },
    mediaOverlay: {
      backgroundLight: colorPalettes.black[300],
      backgroundMedium: colorPalettes.black[500],
      foreground: colorPalettes.white.default,
      // Letterbox backdrop behind a video frame. Deliberately identical in
      // light and dark: it surrounds the video itself, so it stays black in
      // both themes rather than following the page surface.
      videoSurface: colorPalettes.black.default,
      // Unplayed portion of a scrub bar. Sits over the video surface above,
      // so it is a translucent white in both themes for the same reason.
      scrubTrack: colorPalettes.white[300],
    },
    system: {
      positive_subtle: colorPalettes.success[800],
    },
  },
  spacing,
  gap: (v: number) => v * 8,
  shadows,
  zIndex,
} as const;

// ---------------------------------------------------------------------------
// Derived types & configuration objects
// ---------------------------------------------------------------------------

export const appThemes = {
  light: lightTheme,
  dark: darkTheme,
} as const;

export const breakpoints = {
  xs: 0,
  sm: 300,
  md: 500,
  lg: 800,
  xl: 1200,
} as const;

export type AppThemes = typeof appThemes;
export type AppBreakpoints = typeof breakpoints;
export type AppColorPalette = keyof AppThemes["light"]["colors"];
export type AppColorPaletteShades = keyof typeof colorPalettes;
export type AppSpacing = keyof typeof spacing;

// ---------------------------------------------------------------------------
// Unistyles module augmentation — makes useUnistyles() and StyleSheet.create
// return correctly typed theme objects in any package that imports this module.
// ---------------------------------------------------------------------------

declare module "react-native-unistyles" {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- required by react-native-unistyles augmentation pattern
  export interface UnistylesThemes extends AppThemes {}
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- required by react-native-unistyles augmentation pattern
  export interface UnistylesBreakpoints extends AppBreakpoints {}
}
